import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

marked.setOptions({ gfm: true, breaks: true })

const renderer = new marked.Renderer()
renderer.table = (token) => {
  const renderCell = (cell) => marked.parseInline(cell.text || "")

  // Info tables (booking summaries, confirmations) have field-label first cells
  // like **Reference Code**, **Treatment**, etc. Don't make those rows clickable.
  const firstCellText = token.rows[0]?.[0]?.text || ""
  const isInfoTable = firstCellText.startsWith("**") || firstCellText.endsWith(":")

  const header = token.header.map(cell => `<th>${renderCell(cell)}</th>`).join("")
  const rows = token.rows.map(row => {
    const label = row[0]?.text?.replace(/\*\*/g, "") || ""
    const cells = row.map(cell => `<td>${renderCell(cell)}</td>`).join("")
    if (isInfoTable) {
      return `<tr>${cells}</tr>`
    }
    return `<tr class="zenna-table-row" data-zenna-pick="${label}" title="Tap to select">${cells}</tr>`
  }).join("")

  const hasHeader = !isInfoTable && token.header.some(cell => cell.text?.trim())
  const thead = hasHeader ? `<thead><tr>${header}</tr></thead>` : ""
  return `<table class="zenna-table">${thead}<tbody>${rows}</tbody></table>`
}
marked.use({ renderer })

export default class extends Controller {
  static targets = [
    "splash", "modePicker", "moodPanel", "treatmentBrowser", "chatPanel",
    "messages", "input", "sendButton", "typing", "typingText", "statusDot"
  ]
  static values = { locationSlug: String, locationName: String, moodPicks: Object, whatsapp: String }

  connect() {
    this._previousPanel = null
    this._pendingMood  = null
    this._pendingPicks = null
    // Lock page scroll for the Zenna full-screen UI — restored in disconnect()
    // so it doesn't bleed into subsequent Turbo navigations
    document.documentElement.style.cssText += ";position:fixed;width:100%;height:100%;overflow:hidden;"
    document.body.style.cssText += ";position:fixed;width:100%;height:100%;overflow:hidden;"
    this._showPanel("splash")
    this._scheduleOpeningSound()
    setTimeout(() => this._fadeToPanel("modePicker"), 2500)
  }

  disconnect() {
    document.documentElement.style.position = ""
    document.documentElement.style.width = ""
    document.documentElement.style.height = ""
    document.documentElement.style.overflow = ""
    document.body.style.position = ""
    document.body.style.width = ""
    document.body.style.height = ""
    document.body.style.overflow = ""
  }

  // ─── Opening sound ────────────────────────────────────────

  _scheduleOpeningSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const play = () => {
        if (ctx.state === "suspended") ctx.resume().then(() => this._playGamelanChime(ctx))
        else this._playGamelanChime(ctx)
      }
      if (ctx.state === "running") {
        play()
      } else {
        const once = () => {
          play()
          document.removeEventListener("touchstart", once)
          document.removeEventListener("click", once)
        }
        document.addEventListener("touchstart", once, { once: true })
        document.addEventListener("click", once, { once: true })
      }
    } catch (e) {}
  }

  _playGamelanChime(ctx) {
    const playNote = (freq, time, dur, vol = 0.18) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.type = "sine"
      osc.frequency.value = freq
      gain.gain.setValueAtTime(vol, time)
      gain.gain.exponentialRampToValueAtTime(0.001, time + dur)
      osc.start(time); osc.stop(time + dur + 0.05)
    }
    const t = ctx.currentTime
    // Gamelan-like: slightly detuned pairs create the characteristic Balinese beating
    playNote(220,  t,       2.0, 0.14)  // A3 base
    playNote(222,  t,       2.0, 0.09)  // A3 +2Hz (beat effect)
    playNote(330,  t + 0.4, 1.5, 0.10) // E4 fifth
    playNote(440,  t + 0.8, 1.2, 0.07) // A4 octave
    playNote(660,  t + 1.1, 0.9, 0.05) // E5
  }

  // ─── Mode picker ──────────────────────────────────────────

  chooseZenna() {
    this._fadeToPanel("moodPanel")
  }

  chooseBrowse() {
    this._previousPanel = null
    this._fadeToPanel("treatmentBrowser")
  }

  backToMode() {
    this._fadeToPanel("modePicker")
  }

  // ─── Cross-navigation ─────────────────────────────────────

  // Opens treatment browser from any panel, remembers where to return
  openBrowserFrom() {
    const current = this._allPanels().find(p => !this[`${p}Target`].classList.contains("hidden"))
    this._previousPanel = current || null
    this._fadeToPanel("treatmentBrowser")
  }

  // Opens treatment browser from chat panel (e.g. header icon tap)
  openBrowserFromChat() {
    this._previousPanel = "chatPanel"
    this._fadeToPanel("treatmentBrowser")
  }

  // Context-aware back: returns to where the guest came from
  backToPrevious() {
    this._fadeToPanel(this._previousPanel || "modePicker")
    this._previousPanel = null
  }

  // ─── Zenna path ───────────────────────────────────────────

  selectMood(event) {
    const mood    = event.currentTarget.dataset.mood
    const moodKey = event.currentTarget.dataset.moodKey
    const picks   = this.moodPicksValue[moodKey]

    this._fadeToPanel("chatPanel")

    if (picks && picks.length > 0) {
      // Instant pre-rendered greeting — no API call yet
      this._appendZennaGreeting()
      this._appendQuickPicks(picks)
      // Stash context; API fires only when guest taps a pick (or types)
      this._pendingMood  = mood
      this._pendingPicks = picks
    } else {
      this._initSession(mood)
    }
  }

  skipToChat() {
    this._fadeToPanel("chatPanel")
    this._initSession(null)
  }

  // ─── Chat ─────────────────────────────────────────────────

  async _initSession(mood) {
    this._appendZennaMessage(`Welcome to ${this.locationNameValue}! I'm Zenna, your personal spa guide. Give me just a moment…`)
    this._setLoading(true)
    await this._sendMessage(mood ? `__INIT__:${mood}` : "__INIT__")
  }

  async send(event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if (!message) return
    this._appendUserMessage(message)
    this.inputTarget.value = ""
    this._autoResize()
    this._setLoading(true)

    if (this._pendingMood) {
      // Guest typed instead of clicking a pick — init session with picks context, then send message
      const picksNames = this._pendingPicks.map(p => p.name).join(", ")
      const initMsg    = `__INIT__:${this._pendingMood}|picks:${picksNames}`
      this._pendingMood  = null
      this._pendingPicks = null
      await this._sendMessage(initMsg)
      await this._sendMessage(message)
    } else {
      await this._sendMessage(message)
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send(event)
    }
  }

  pickOption(event) {
    const row = event.target.closest("[data-zenna-pick]")
    if (!row) return
    const label = row.dataset.zennaPick
    if (!label) return

    // Highlight selected row (tables) or freeze the whole picks card (quick pick divs)
    const isTableRow = row.tagName === "TR"
    if (isTableRow) {
      row.style.background = "#4A524A"
      row.querySelectorAll("td").forEach(td => td.style.color = "white")
    } else {
      const card = row.closest(".rounded-xl")
      if (card) {
        card.style.pointerEvents = "none"
        row.style.background = "#4A524A"
        row.querySelectorAll("p").forEach(p => p.style.color = "white")
        row.querySelector("svg")?.remove()
      }
    }

    this._appendUserMessage(`I'm interested in ${label}`)
    this._setLoading(true)

    if (this._pendingMood) {
      // First interaction after seeing picks — init session with full context
      const picksNames = this._pendingPicks.map(p => p.name).join(", ")
      const initMsg    = `__INIT__:${this._pendingMood}|picks:${picksNames}|selected:${label}`
      this._pendingMood  = null
      this._pendingPicks = null
      this._sendMessage(initMsg)
    } else {
      this._sendMessage(`I'm interested in ${label}`)
    }
  }

  inputTargetConnected(el) {
    el.addEventListener("input", () => this._autoResize())
  }

  _autoResize() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 120) + "px"
  }

  // ─── Panel transitions ────────────────────────────────────

  _allPanels() {
    return ["splash", "modePicker", "moodPanel", "treatmentBrowser", "chatPanel"]
  }

  _showPanel(name) {
    this._allPanels().forEach(p => {
      const el = this[`${p}Target`]
      const active = p === name
      el.classList.toggle("hidden", !active)
      el.style.opacity = active ? "1" : "0"
    })
  }

  _fadeToPanel(name) {
    // Find currently visible panel
    const current = this._allPanels().find(p => !this[`${p}Target`].classList.contains("hidden"))

    if (current) {
      const outEl = this[`${current}Target`]
      outEl.style.opacity = "0"
      setTimeout(() => {
        outEl.classList.add("hidden")
        this._revealPanel(name)
      }, 450)
    } else {
      this._revealPanel(name)
    }
  }

  _revealPanel(name) {
    const el = this[`${name}Target`]
    el.classList.remove("hidden")
    requestAnimationFrame(() => { el.style.opacity = "1" })
  }

  // ─── Networking ───────────────────────────────────────────

  async _sendMessage(message) {
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(`/${this.locationSlugValue}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/event-stream"
        },
        body: JSON.stringify({ message })
      })
      if (!response.ok) {
        this._appendZennaMessage("I'm having trouble connecting. Please try again.")
        return
      }
      await this._consumeStream(response)
    } catch {
      this._appendZennaMessage("Sorry, I couldn't connect. Please check your network.")
    } finally {
      this._setLoading(false)
    }
  }

  async _consumeStream(response) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop()
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const raw = line.slice(6).trim()
          if (!raw) continue
          let chunk
          try { chunk = JSON.parse(raw) } catch { continue }
          if (chunk.type === "status")        this._setTypingText(chunk.message)
          else if (chunk.type === "complete")    this._appendZennaMessage(chunk.content || "")
          else if (chunk.type === "service_down") this._appendServiceDown()
          else if (chunk.type === "error")       this._appendZennaMessage(chunk.message || "Something went wrong.")
        }
      }
    } finally {
      reader.releaseLock()
    }
  }

  // ─── Message rendering ────────────────────────────────────

  _appendServiceDown() {
    const waNumber = this.whatsappValue
    const waLink   = waNumber ? `https://wa.me/${waNumber}` : null

    const div = document.createElement("div")
    div.className = "flex items-start gap-2"
    div.innerHTML = `
      <div class="w-7 h-7 bg-zenna rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
          <path d="M9.5 2a1 1 0 0 1 .95.684l.39 1.132a3.2 3.2 0 0 0 1.994 1.994l1.132.39a1 1 0 0 1 0 1.893l-1.132.39a3.2 3.2 0 0 0-1.994 1.994l-.39 1.132a1 1 0 0 1-1.893 0l-.39-1.132A3.2 3.2 0 0 0 5.224 7.09l-1.132-.39a1 1 0 0 1 0-1.893l1.132-.39A3.2 3.2 0 0 0 7.218 2.42l.39-1.132A1 1 0 0 1 9.558 2Z" fill="white"/>
          <path d="M18 12a1 1 0 0 1 .946.677l.253.734a2.4 2.4 0 0 0 1.496 1.496l.734.253a1 1 0 0 1 0 1.893l-.734.253a2.4 2.4 0 0 0-1.496 1.496l-.253.734a1 1 0 0 1-1.893 0l-.253-.734a2.4 2.4 0 0 0-1.496-1.496l-.734-.253a1 1 0 0 1 0-1.893l.734-.253a2.4 2.4 0 0 0 1.496-1.496l.253-.734A1 1 0 0 1 18 12Z" fill="white"/>
          <path d="M6 14a1 1 0 0 1 .942.662l.108.312a1.6 1.6 0 0 0 .996.996l.312.108a1 1 0 0 1 0 1.884l-.312.108a1.6 1.6 0 0 0-.996.996l-.108.312a1 1 0 0 1-1.884 0l-.108-.312a1.6 1.6 0 0 0-.996-.996l-.312-.108a1 1 0 0 1 0-1.884l.312-.108a1.6 1.6 0 0 0 .996-.996l.108-.312A1 1 0 0 1 6 14Z" fill="white"/>
        </svg>
      </div>
      <div class="zenna-bubble-zenna rounded-2xl rounded-tl-sm px-4 py-3 text-sm max-w-[85%] shadow-sm">
        <p class="leading-relaxed mb-3">I'm sorry — I'm having a little trouble connecting right now. You can browse our treatments or reach us directly to book.</p>
        <div class="flex flex-col gap-2">
          <button class="w-full py-2 px-3 rounded-lg text-xs font-medium text-center transition-colors"
                  style="background:#4A524A; color:white;"
                  data-action="click->zenna-guest#openBrowserFromChat">
            Browse Treatments
          </button>
          ${waLink ? `
          <a href="${waLink}" target="_blank" rel="noopener"
             class="w-full py-2 px-3 rounded-lg text-xs font-medium text-center transition-colors flex items-center justify-center gap-1.5"
             style="background:#25D366; color:white;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Book via WhatsApp
          </a>` : ""}
        </div>
      </div>`
    this.messagesTarget.appendChild(div)
    this._scrollToBottom()
  }

  _appendZennaGreeting() {
    const hour = new Date().getHours()
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening"
    this._appendZennaMessage(`${greeting}! Welcome to ${this.locationNameValue}. I'm Zenna, your personal spa guide. How can I help you today?`)
  }

  _appendQuickPicks(picks) {
    const rows = picks.map(p =>
      `<div class="flex items-center gap-3 px-3 py-3 border-b last:border-0 cursor-pointer transition-colors hover:bg-[#F5F2ED] active:bg-[#E8EBE8]"
            style="border-color:#F0EDE8;"
            data-zenna-pick="${this._escapeHtml(p.name)}"
            title="Tap to select">
        <div class="flex-1 min-w-0 pointer-events-none">
          <p class="font-medium text-sm leading-tight" style="color:#2D2D2D;">${this._escapeHtml(p.name)}</p>
          <p class="text-xs mt-0.5" style="color:#9BA89B;">${p.duration} min · ${this._escapeHtml(p.price)}</p>
        </div>
        <svg class="w-4 h-4 flex-shrink-0 pointer-events-none" style="color:#9BA89B;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
      </div>`
    ).join("")

    const text = `Here are some treatments that might be perfect for you — tap one to get started:`
    const html = marked.parse(text)
    const div = document.createElement("div")
    div.className = "flex items-start gap-2"
    div.innerHTML = `
      <div class="w-7 h-7 bg-zenna rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
          <path d="M9.5 2a1 1 0 0 1 .95.684l.39 1.132a3.2 3.2 0 0 0 1.994 1.994l1.132.39a1 1 0 0 1 0 1.893l-1.132.39a3.2 3.2 0 0 0-1.994 1.994l-.39 1.132a1 1 0 0 1-1.893 0l-.39-1.132A3.2 3.2 0 0 0 5.224 7.09l-1.132-.39a1 1 0 0 1 0-1.893l1.132-.39A3.2 3.2 0 0 0 7.218 2.42l.39-1.132A1 1 0 0 1 9.558 2Z" fill="white"/>
          <path d="M18 12a1 1 0 0 1 .946.677l.253.734a2.4 2.4 0 0 0 1.496 1.496l.734.253a1 1 0 0 1 0 1.893l-.734.253a2.4 2.4 0 0 0-1.496 1.496l-.253.734a1 1 0 0 1-1.893 0l-.253-.734a2.4 2.4 0 0 0-1.496-1.496l-.734-.253a1 1 0 0 1 0-1.893l.734-.253a2.4 2.4 0 0 0 1.496-1.496l.253-.734A1 1 0 0 1 18 12Z" fill="white"/>
          <path d="M6 14a1 1 0 0 1 .942.662l.108.312a1.6 1.6 0 0 0 .996.996l.312.108a1 1 0 0 1 0 1.884l-.312.108a1.6 1.6 0 0 0-.996.996l-.108.312a1 1 0 0 1-1.884 0l-.108-.312a1.6 1.6 0 0 0-.996-.996l-.312-.108a1 1 0 0 1 0-1.884l.312-.108a1.6 1.6 0 0 0 .996-.996l.108-.312A1 1 0 0 1 6 14Z" fill="white"/>
        </svg>
      </div>
      <div class="zenna-bubble-zenna rounded-2xl rounded-tl-sm px-4 py-3 text-sm max-w-[85%] shadow-sm">
        <div class="zenna-markdown mb-2">${html}</div>
        <div class="rounded-xl overflow-hidden border" style="border-color:#E8EBE8;">${rows}</div>
      </div>`
    this.messagesTarget.appendChild(div)
    this._scrollToBottom()
  }

  _appendUserMessage(text) {
    const div = document.createElement("div")
    div.className = "flex justify-end"
    div.innerHTML = `<div class="zenna-bubble-user rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm max-w-[80%] leading-relaxed">${this._escapeHtml(text)}</div>`
    this.messagesTarget.appendChild(div)
    this._scrollToBottom()
  }

  _appendZennaMessage(text) {
    const html = marked.parse(text)
    const div = document.createElement("div")
    div.className = "flex items-start gap-2"
    div.innerHTML = `
      <div class="w-7 h-7 bg-zenna rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
          <path class="zenna-sparkle-1" d="M9.5 2a1 1 0 0 1 .95.684l.39 1.132a3.2 3.2 0 0 0 1.994 1.994l1.132.39a1 1 0 0 1 0 1.893l-1.132.39a3.2 3.2 0 0 0-1.994 1.994l-.39 1.132a1 1 0 0 1-1.893 0l-.39-1.132A3.2 3.2 0 0 0 5.224 7.09l-1.132-.39a1 1 0 0 1 0-1.893l1.132-.39A3.2 3.2 0 0 0 7.218 2.42l.39-1.132A1 1 0 0 1 9.558 2Z" fill="white"/><path class="zenna-sparkle-2" d="M18 12a1 1 0 0 1 .946.677l.253.734a2.4 2.4 0 0 0 1.496 1.496l.734.253a1 1 0 0 1 0 1.893l-.734.253a2.4 2.4 0 0 0-1.496 1.496l-.253.734a1 1 0 0 1-1.893 0l-.253-.734a2.4 2.4 0 0 0-1.496-1.496l-.734-.253a1 1 0 0 1 0-1.893l.734-.253a2.4 2.4 0 0 0 1.496-1.496l.253-.734A1 1 0 0 1 18 12Z" fill="white"/><path class="zenna-sparkle-3" d="M6 14a1 1 0 0 1 .942.662l.108.312a1.6 1.6 0 0 0 .996.996l.312.108a1 1 0 0 1 0 1.884l-.312.108a1.6 1.6 0 0 0-.996.996l-.108.312a1 1 0 0 1-1.884 0l-.108-.312a1.6 1.6 0 0 0-.996-.996l-.312-.108a1 1 0 0 1 0-1.884l.312-.108a1.6 1.6 0 0 0 .996-.996l.108-.312A1 1 0 0 1 6 14Z" fill="white"/>
        </svg>
      </div>
      <div class="zenna-bubble-zenna rounded-2xl rounded-tl-sm px-4 py-3 text-sm max-w-[85%] shadow-sm">
        <div class="zenna-markdown">${html}</div>
      </div>`
    this.messagesTarget.appendChild(div)
    this._scrollToBottom()
  }

  _setLoading(loading) {
    this.sendButtonTarget.disabled = loading
    this.inputTarget.disabled = loading
    this.typingTarget.classList.toggle("hidden", !loading)
    if (!loading) {
      this._setTypingText("Zenna is thinking...")
      this.inputTarget.focus()
    }
    this._scrollToBottom()
  }

  _setTypingText(text) {
    if (this.hasTypingTextTarget) this.typingTextTarget.textContent = text
  }

  _scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
